// src/shared/components/CsvDownloadButton.tsx

import { useState } from 'react';
import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDownload } from '@tabler/icons-react';
import { ApiError } from '@/shared/api/ApiError';
import { triggerBlobDownload } from '@/shared/utils/download';
import type { BlobResponse } from '@/shared/api/WebApi';

interface CsvDownloadButtonProps {
  /** Runs the authenticated blob download. */
  download: () => Promise<BlobResponse>;
  /** Used when the response carries no Content-Disposition filename. */
  fallbackFilename: string;
  label?: string;
}

/**
 * Generic CSV download button: busy state + error toast (server validation
 * errors arrive as JSON and surface as a toast, never a corrupt file).
 */
export function CsvDownloadButton({
  download,
  fallbackFilename,
  label = 'Download CSV',
}: CsvDownloadButtonProps) {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleClick = async () => {
    setIsDownloading(true);
    try {
      const res = await download();
      triggerBlobDownload(res, fallbackFilename);
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'CSV download failed',
        message: error instanceof ApiError ? error.message : 'Please try again.',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      variant="default"
      size="xs"
      leftSection={<IconDownload size={14} />}
      loading={isDownloading}
      onClick={handleClick}
    >
      {label}
    </Button>
  );
}
